import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import styled, { css, keyframes, useTheme } from 'styled-components'

import SvgIcon from '@components/SvgIcon'
import { mobileCSS } from '@measures/responsive'

import { SnackbarItem as SnackbarItemType } from '@hooks/useSnackbar'

const AUTO_HIDE_DURATION = 3000
const FADE_OUT_TIMEOUT = 300

interface SnackbarItemProps {
  snackbar: SnackbarItemType
  onHide: (id: string) => void
}

const slideIn = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`

const slideOut = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
`

type SnackbarWrapperProps = Pick<SnackbarItemType, 'variant'> & {
  isFadingOut: boolean
}

const SnackbarWrapper = styled.div<SnackbarWrapperProps>`
  ${({
    theme: {
      colors: {
        strokeSportHover,
        strokeBrandHover,
        fillSportWeak,
        fillBrandWeak,
      },
      spacing: { spacing8, spacing16, spacing24 },
    },
    variant,
    isFadingOut,
  }) => css`
    border-radius: 8px;
    padding: ${spacing8};
    gap: ${spacing8};
    margin-bottom: ${spacing8};

    &:last-child {
      margin-bottom: ${spacing24};
    }

    display: flex;
    align-items: center;
    box-sizing: border-box;

    animation: ${slideIn} 0.3s ease-out;
    width: fit-content;
    min-width: 100px;
    max-width: 500px;

    ${mobileCSS(css`
      width: calc(100vw - ${spacing16} * 2);
      max-width: auto;
    `)}

    ${isFadingOut &&
    css`
      animation: ${slideOut} 0.3s ease-in forwards;
    `}

    ${variant === 'success' &&
    css`
      border: 1px solid ${strokeSportHover};
      background-color: ${fillSportWeak};
    `}

    ${variant === 'error' &&
    css`
      border: 1px solid ${strokeBrandHover};
      background-color: ${fillBrandWeak};
    `}
  `}
`

type SnackbarMessageProps = Pick<SnackbarWrapperProps, 'variant'>

const SnackbarMessage = styled.div<SnackbarMessageProps>`
  ${({
    theme: {
      typography: {
        catchword: {
          large2: { bundledCSS: catchwordLarge2 },
        },
      },
      colors: { textSport, textBrand },
      utils: { ellipsis },
    },
    variant,
  }) => css`
    flex: 1;
    ${catchwordLarge2};
    color: ${variant === 'success' ? textSport : textBrand};

    ${ellipsis('100%', 2)};
  `}
`

const SnackbarItem: FunctionComponent<SnackbarItemProps> = ({
  snackbar,
  onHide,
}) => {
  const theme = useTheme()
  const [isFadingOut, setIsFadingOut] = useState(false)

  const autoHideTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const fadeOutTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const hideItem = useCallback(() => {
    setIsFadingOut(true)

    fadeOutTimeoutRef.current = setTimeout(() => {
      onHide(snackbar.id)
      setIsFadingOut(false)
      fadeOutTimeoutRef.current = undefined
    }, FADE_OUT_TIMEOUT)
  }, [snackbar.id, onHide])

  useEffect(() => {
    if (snackbar.autoHide) {
      autoHideTimeoutRef.current = setTimeout(() => {
        hideItem()
        autoHideTimeoutRef.current = undefined
      }, AUTO_HIDE_DURATION)
    }
  }, [hideItem, snackbar.autoHide])

  useEffect(() => {
    return () => {
      if (autoHideTimeoutRef.current) {
        clearTimeout(autoHideTimeoutRef.current)
      }
      if (fadeOutTimeoutRef.current) {
        clearTimeout(fadeOutTimeoutRef.current)
      }
    }
  }, [])

  const iconName =
    snackbar.variant === 'success' ? 'check-circle-solid' : 'circle-xmark-solid'

  const iconColor =
    snackbar.variant === 'success'
      ? theme.colors.iconSport
      : theme.colors.iconBrand

  return (
    <SnackbarWrapper variant={snackbar.variant} isFadingOut={isFadingOut}>
      <SvgIcon iconName={iconName} size={24} color={iconColor} />
      <SnackbarMessage variant={snackbar.variant}>
        {snackbar.message}
      </SnackbarMessage>
    </SnackbarWrapper>
  )
}

export default SnackbarItem
